import React from 'react';
import {
  Create,
  TextInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SelectArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  BooleanInput,
  required,
  email,
  SaveButton,
  TopToolbar,
  useRecordContext,
  useResourceContext,
  AutocompleteInput,
} from 'react-admin';
import { PROVIDER_ROLES_RESOURCE_NAME as PROVIDER_ROLES_RESOURCE } from '../../roles/provider.roles.dataProvider';
import { NON_PROVIDER_ROLES_RESOURCE_NAME as NON_PROVIDER_ROLES_RESOURCE } from '../../roles/non.provider.roles.dataProvider';
import { ALL_ORGANIZATIONS_RESOURCE_NAME as ORGANIZATIONS_RESOURCE } from '../../organizations/organizations.dataProvider';
import { US_STATES_RESOURCE_NAME as US_STATES_RESOURCE } from '../../usStates/usStates.dataProvider';
import { LANGUAGES_RESOURCE_NAME as LANGUAGES_RESOURCE } from '../../languages/languages.dataProvider';
import { PERMISSION_TEMPLATES_RESOURCE_NAME as PERMISSION_TEMPLATE_RESOURCE } from '../../permissionTemplates/permission.template.dataProvider';
import { userFields } from '../user.model';
import { USER_ROLE_RESOURCE_MAPPER } from '../constants';
import { ORGANIZATION_DROPDOWN_PERPAGE } from '../../utils/constants';
import { rolesFields } from '../../roles/roles.model';
import { userRelativeFields } from '../userRelative.model';
import { validatePhone } from '../../utils/validation';
import { optionalField } from '../../utils/constants';
import CustomDatePicker from '../../app/customDatePicker';
import { useCreateWithRefresh } from '../../hooks/useMutationWithRefresh';
import { SectionedForm } from '../../components/layouts';

const CreateActions = () => (
  <TopToolbar>
    <SaveButton />
  </TopToolbar>
);

const validateEmail = [required(), email()];
const validateRequired = [required()];

const UserCreate = () => {
  const resource = useResourceContext();

  // Determine user type based on resource
  const isProvider = resource === 'providers';
  const isPatient = resource === 'patients';
  const isAdmin = resource === 'admins';

  const mutationOptions = useCreateWithRefresh(resource, {
    successMessage: 'User created successfully',
  });

  // Build role-specific fields
  const getRoleSpecificFields = () => {
    const fields = [];

    // Provider-specific fields
    if (isProvider) {
      fields.push(
        <ReferenceInput
          key="permissionTemplate"
          source={userFields.permissionTemplateId}
          reference={PERMISSION_TEMPLATE_RESOURCE}
          label="Permission Template (from Healthie)"
        >
          <SelectInput
            optionText="name"
            validate={required()}
          />
        </ReferenceInput>,
        <ReferenceArrayInput
          key="providerRoles"
          source={userFields.providerRoleIds}
          reference={PROVIDER_ROLES_RESOURCE}
          label="Provider Roles"
        >
          <SelectArrayInput
            optionText={rolesFields.roleName}
          />
        </ReferenceArrayInput>,
        <TextInput
          key="providerBio"
          source={userFields.providerBio}
          label="Provider Bio"
          multiline
          rows={5}
          resettable
        />,
        <TextInput
          key="providerBioEs"
          source={userFields.providerBioEs}
          label="Provider Bio (Spanish)"
          multiline
          rows={5}
          resettable
        />,
        <TextInput
          key="zipCode"
          source={userFields.zipCode}
          label="ZIP code"
        />
      );
    }

    // Patient-specific fields
    if (isPatient) {
      fields.push(
        <CustomDatePicker
          key="dob"
          source={userFields.dob}
          label="DOB"
        />,
        <CustomDatePicker
          key="dueDate"
          source={userFields.dueDate}
          label="Due date"
        />,
        <ReferenceInput
          key="organization"
          source={userFields.orgId}
          reference={ORGANIZATIONS_RESOURCE}
          perPage={ORGANIZATION_DROPDOWN_PERPAGE}
          sort={{ field: 'name', order: 'ASC' }}
          label="Organization"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>,
        <TextInput
          key="wicId"
          source={userFields.wicId}
          label="WIC ID"
        />
      );
    }

    // Non-admin fields (patients and providers)
    if (!isAdmin) {
      fields.push(
        <ReferenceInput
          key="state"
          source={userFields.usState}
          reference={US_STATES_RESOURCE}
          label="State"
        >
          <SelectInput
            optionText="name"
            validate={validateRequired}
          />
        </ReferenceInput>
      );
    }

    return fields;
  };

  const sections = [
    {
      title: "Basic Information",
      fields: [
        <ReferenceInput
          key="role"
          source={userFields.roleId}
          reference={NON_PROVIDER_ROLES_RESOURCE}
          filter={{ name: USER_ROLE_RESOURCE_MAPPER[resource] }}
          label="Role"
        >
          <SelectInput
            optionText="name"
            validate={validateRequired}
          />
        </ReferenceInput>,
        <TextInput
          key="firstName"
          source={userFields.firstName}
          label="First Name"
          validate={validateRequired}
        />,
        <TextInput
          key="lastName"
          source={userFields.lastName}
          label="Last Name"
          validate={validateRequired}
        />,
        <TextInput
          key="email"
          source={userFields.email}
          label="Email"
          validate={validateEmail}
        />,
        <TextInput
          key="phoneNumber"
          source={userFields.phoneNumber}
          label="Phone Number"
          validate={[
            (isProvider || isAdmin) ? required() : optionalField,
            validatePhone(),
          ]}
        />,
        <ReferenceInput
          key="language"
          source={userFields.lang}
          reference={LANGUAGES_RESOURCE}
          label="Language"
        >
          <SelectInput
            optionText="name"
            validate={required()}
          />
        </ReferenceInput>
      ]
    },
    {
      title: "Role-Specific Information",
      fields: getRoleSpecificFields()
    },
    {
      title: "Settings",
      fields: [
        <BooleanInput
          key="isActive"
          source={userFields.isActive}
          defaultValue={true}
          label="Active"
        />,
        ...(isPatient ? [] : [
          <BooleanInput
            key="mfaRequired"
            source={userFields.isMfaRequired}
            label="MFA Required"
          />
        ])
      ]
    },
    ...(isPatient ? [{
      title: "User Relatives",
      fields: [
        <ArrayInput key="relatives" source={userFields.relatives} label="Relatives">
          <SimpleFormIterator>
            <TextInput
              label="Name"
              validate={validateRequired}
              source={userRelativeFields.name}
            />
          </SimpleFormIterator>
        </ArrayInput>
      ]
    }] : [])
  ];

  return (
    <Create mutationOptions={mutationOptions}>
      <SectionedForm sections={sections} />
    </Create>
  );
};

export default UserCreate;
